import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { INTRO } from "@/lib/brand";

/**
 * Bird flight intro animation — premium entrance.
 *
 * Tunable from /lib/brand.js (INTRO config):
 *   - enabled            master kill switch
 *   - replayMode         'always' | 'session-once'
 *   - *DurationSec       per-device flight duration
 *
 * The Brand component's logo img must carry [data-brand-logo] and the text
 * span must carry [data-brand-text] for the hide/reveal CSS in index.css to work.
 *
 * RULES (see CONTRIBUTING.md):
 *   - Do not animate layout-affecting properties (top/left/width/height) after init.
 *   - Only transform + opacity.
 *   - Must not block clicks (pointer-events: none).
 *   - Must auto-cleanup even if onAnimationComplete is never fired.
 */

const SESSION_KEY = "ftsBirdIntroPlayed";
const LOGO_SRC = "/logo.png";
const LOGO_ASPECT = 1024 / 812;
const SAFETY_TIMEOUT_MS = 4000;

export default function BirdFlightIntro() {
  const [cfg, setCfg] = useState(null);
  const finishedRef = useRef(false);
  const safetyTimerRef = useRef(null);

  useEffect(() => {
    if (!INTRO.enabled || typeof window === "undefined") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    try { if (sessionStorage.getItem(SESSION_KEY)) return; } catch (e) { /* private mode */ }

    // Hide the navbar logo + text while the bird flies in.
    document.body.classList.add("bird-intro-active");

    // Measure the target on the next frame, with a few retries in case
    // <Brand /> mounts a tick later (e.g., Suspense or font swap).
    let cancelled = false;
    let attempts = 0;
    const tryMeasure = () => {
      if (cancelled) return;
      const el = document.querySelector("[data-brand-logo]");
      if (!el) {
        if (++attempts > 20) {
          // give up gracefully
          document.body.classList.remove("bird-intro-active");
          return;
        }
        requestAnimationFrame(tryMeasure);
        return;
      }
      const r = el.getBoundingClientRect();
      // If the element hasn't laid out yet (width=0), retry briefly
      if (r.width < 4) {
        if (++attempts > 20) {
          document.body.classList.remove("bird-intro-active");
          return;
        }
        requestAnimationFrame(tryMeasure);
        return;
      }
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const isMobile = vw < 768;
      const overlayH = isMobile ? 90 : 130;
      const overlayW = overlayH * LOGO_ASPECT;
      const duration = isMobile ? INTRO.mobileDurationSec : INTRO.desktopDurationSec;
      setCfg({
        target: { left: r.left, top: r.top, width: r.width, height: r.height },
        vw, vh, isMobile, overlayW, overlayH, duration,
      });
    };
    requestAnimationFrame(tryMeasure);

    // Absolute safety: never leave the logo hidden longer than this
    safetyTimerRef.current = window.setTimeout(() => {
      if (!finishedRef.current) {
        document.body.classList.remove("bird-intro-active");
        try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (e) { /* noop */ }
        finishedRef.current = true;
        setCfg((c) => (c ? { ...c, done: true } : { done: true }));
      }
    }, SAFETY_TIMEOUT_MS);

    return () => {
      cancelled = true;
      if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
      if (!finishedRef.current) document.body.classList.remove("bird-intro-active");
    };
  }, []);

  const handleComplete = () => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    if (safetyTimerRef.current) clearTimeout(safetyTimerRef.current);
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (e) { /* noop */ }
    document.body.classList.remove("bird-intro-active");
    setCfg((c) => (c ? { ...c, done: true } : null));
  };

  if (!cfg || cfg.done) return null;
  const { target, vw, vh, overlayW, overlayH, isMobile, duration } = cfg;

  // Final transform values — overlay's visual top-left should land on the target's top-left,
  // with overlay scaled down to match the target's size. transform-origin: center.
  const finalScale = target.height / overlayH;
  const finalX = target.left + (target.width - overlayW) / 2;
  const finalY = target.top + (target.height - overlayH) / 2;

  const startX = -overlayW - 40;
  const startY = vh * 0.5 - overlayH / 2;

  // Two waypoints for the organic arc
  const midX1 = vw * (isMobile ? 0.22 : 0.28);
  const midY1 = vh * (isMobile ? 0.26 : 0.18);
  const midX2 = vw * (isMobile ? 0.55 : 0.6);
  const midY2 = Math.max(target.top - overlayH * 0.55, 40);

  return (
    <motion.img
      src={LOGO_SRC}
      alt=""
      aria-hidden="true"
      draggable="false"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: overlayW,
        height: overlayH,
        objectFit: "contain",
        pointerEvents: "none",
        zIndex: 200,
        willChange: "transform, opacity",
        transformOrigin: "center",
        userSelect: "none",
        // soft cinematic glow to add depth without being cheesy
        filter: "drop-shadow(0 8px 20px rgba(10,25,48,0.18))",
      }}
      initial={{ x: startX, y: startY, opacity: 0, rotate: -12, scale: 1.15 }}
      animate={{
        x: [startX, midX1, midX2, finalX],
        y: [startY, midY1, midY2, finalY],
        rotate: [-12, 6, -4, 0],
        scale: [1.15, 1.08, 0.92, finalScale],
        opacity: [0, 1, 1, 1],
      }}
      transition={{
        duration,
        times: [0, 0.28, 0.7, 1],
        ease: [0.16, 1, 0.3, 1],
      }}
      onAnimationComplete={handleComplete}
      data-testid="bird-intro"
    />
  );
}
